import { Box, Button, Flex, IconButton, Text, TextInput } from 'pounceV2';
import React from 'react';
import { useTheme } from '@mui/material';
import { useQuoteStyle } from '../whitespace';

interface ValueInputProps<T> {
  initialValue: T;
  onSave: (prevV: T, newV: T) => void;
  onCancel: () => void;
  validateItem: (v: T) => string | null;
}

function MultiValueItemEditV3<T>({
  initialValue,
  onSave,
  onCancel,
  validateItem,
}: ValueInputProps<T>) {
  const {
    palette: { tokens },
  } = useTheme();
  const [editValue, setEditValue] = React.useState<T>(initialValue);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (initialValue === editValue) {
      // We need to reset error every time editValue matches the initial
      setError(null);
    }
  }, [editValue, validateItem, initialValue]);

  const onSaveClick = React.useCallback(() => {
    setError(null);
    const validationError = validateItem(editValue);

    if (validationError) {
      setError(validationError);
    } else {
      onSave(initialValue, editValue);
    }
  }, [setError, onSave, initialValue, editValue, validateItem]);

  const handleSubmit = React.useCallback(
    event => {
      event.preventDefault();
      onSaveClick();
      return false;
    },
    [onSaveClick],
  );

  const quoteSx = useQuoteStyle();
  const isString = typeof initialValue === 'string';

  return (
    <Flex
      align="center"
      py={2}
      borderBottom={`1px solid ${tokens.stroke.neutral[5]}`}
      gap={2}
      as="form"
      onSubmit={handleSubmit}
    >
      <Flex flex={1} align="center" gap={1}>
        {isString && (
          <Text as="span" sx={quoteSx} aria-hidden="true">
            &ldquo;
          </Text>
        )}
        <Box flex={1}>
          <TextInput
            label=""
            value={editValue as any}
            onChange={v => setEditValue(v.target.value as T)}
            data-testid="entry-input"
          />
          {error && (
            <Text color={tokens.content.text.semantic.error.base} variant="body/sm" pt={1} pl={2}>
              {error}
            </Text>
          )}
        </Box>
        {isString && (
          <Text as="span" sx={quoteSx} aria-hidden="true">
            &rdquo;
          </Text>
        )}
      </Flex>
      <IconButton
        aria-label="cancel changes"
        variant="outline"
        icon="close"
        size="medium"
        onClick={onCancel}
      />
      <Button
        onClick={onSaveClick}
        variant="outline"
        disabled={!editValue || editValue === initialValue}
      >
        Save
      </Button>
    </Flex>
  );
}

export default MultiValueItemEditV3;
